/**
 * WHAT: Main application shell with role-based routing, top navigation, and view rendering.
 * WHY:  Acts as the single entry point for all authenticated DAMA users, enforcing RBAC
 *       permissions on every navigation target so that DBAs, campaign managers, attesters,
 *       and dev admins each see only the features they are authorized to use.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './colorful-theme.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import ApplicationDetails from './components/ApplicationDetails';
import AppDatabaseMapping from './components/AppDatabaseMapping';
import UARCampaigns from './components/UARCampaigns';
import CampaignWorkflow from './components/CampaignWorkflow';
import AttesterOverride from './components/AttesterOverride';
import DatabaseOwners from './components/DatabaseOwners';
import ManageAttestations from './components/ManageAttestations';
import AttesterReplacement from './components/AttesterReplacement';
import AttestationActions from './components/AttestationActions';
import ManagerAttestations from './components/ManagerAttestations';
import ComplianceFrameworks from './components/ComplianceFrameworks';
import DatabaseTypes from './components/DatabaseTypes';
import SecretSettings from './components/SecretSettings';
import DatabaseConnections from './components/DatabaseConnections';
import OracleConnections from './components/OracleConnections';
import MssqlConnections from './components/MssqlConnections';
import MySqlConnections from './components/MySqlConnections';
import CassandraConnections from './components/CassandraConnections';
import PostgresqlConnections from './components/PostgresqlConnections';
import DatabaseRefresh from './components/DatabaseRefresh';
import RefreshEnterpriseUsers from './components/RefreshEnterpriseUsers';
import Reports from './components/Reports';
import SelectCampaign from './components/SelectCampaign';
import ManagePrivilegeMappings from './components/ManagePrivilegeMappings';
import DBComplianceMatrix from './components/DBComplianceMatrix';
import PrivilegeMatrix from './components/PrivilegeMatrix';
import AIQueryAssistant from './components/AIQueryAssistant';
import OracleUserAccessReport from './components/OracleUserAccessReport';
import MySQLUserAccessReport from './components/MySQLUserAccessReport';
import CassandraUserAccessReport from './components/CassandraUserAccessReport';
import MSSQLUserAccessReport from './components/MSSQLUserAccessReport';
import MSSQLGroupAccessReport from './components/MSSQLGroupAccessReport';
import PostgresqlUserAccessReport from './components/PostgresqlUserAccessReport';
import LateralMovementReport from './components/LateralMovementReport';
import EnterpriseUsersReport from './components/EnterpriseUsersReport';
import CAReport from './components/CAReport';
import CAReportMSSQL from './components/CAReportMSSQL';
import AzureADGroups from './components/AzureADGroups';
import UserManagement from './components/UserManagement';
import PreAttestationBaselineReport from './components/PreAttestationBaselineReport';
import CampaignBaselineReport from './components/CampaignBaselineReport';
import PostAttestationReport from './components/PostAttestationReport';
import AttesterAuditReport from './components/AttesterAuditReport';
import NotificationManager from './components/NotificationManager';
import CustomSqlExecutor from './components/CustomSqlExecutor';
import DamaPro from './components/DamaPro';
import ErrorBoundary from './components/ErrorBoundary';
import { RetentionSettings } from './components/CampaignEvidence';
// Password Policy Compliance Components
import {
  OraclePasswordCompliance,
  MssqlPasswordCompliance,
  MysqlPasswordCompliance,
  PostgresqlPasswordCompliance,
  CassandraPasswordCompliance
} from './components/compliance';
import { PostgresqlIcon } from './components/common';
import InactivityWarningModal from './components/InactivityWarningModal';
import useInactivityLogout from './hooks/useInactivityLogout';
import API_CONFIG from './config/api.config';

// Build version for cache busting
const BUILD_VERSION = "2026.01.24.v2";
console.log("DAMA Build:", BUILD_VERSION);

/**
 * WHAT: Primary authenticated layout containing the top nav bar and role-gated view router.
 * WHY:  Centralizes all post-login navigation so that permission checks, URL syncing, and
 *       inactivity logout are handled in one place rather than scattered across components.
 */
const MainApp = () => {
  const {
    user,
    logout,
    hasPermission,
    canManageConnections,
    canManageRefreshJobs,
    canManagePrivilegeMappings,
    canViewComplianceReports,
    canManageAnyConnections,
    getAccessibleTechnologies
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Convert URL path to view name
  const getViewFromPath = useCallback((pathname) => {
    const path = pathname.replace(/^\//, '') || 'dashboard';
    return path;
  }, []);

  // Initialize currentView from URL
  const [currentView, setCurrentViewState] = useState(() => getViewFromPath(location.pathname));
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deepLinkAppName, setDeepLinkAppName] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubDropdown, setOpenSubDropdown] = useState(null);

  // Custom setCurrentView that also updates the URL
  const setCurrentView = useCallback((view) => {
    setCurrentViewState(view);
    const newPath = `/${view}`;
    if (location.pathname !== newPath) {
      navigate(newPath, { replace: false });
    }
  }, [navigate, location.pathname]);

  // Sync view state when URL changes (back/forward button)
  useEffect(() => {
    const viewFromUrl = getViewFromPath(location.pathname);
    if (viewFromUrl !== currentView) {
      setCurrentViewState(viewFromUrl);
    }
  }, [location.pathname, getViewFromPath, currentView]);

  // Timer ref for delayed closing of submenus
  const submenuCloseTimer = React.useRef(null);

  // Auto-logout on inactivity
  const handleInactivityLogout = useCallback(() => {
    logout(navigate);
  }, [logout, navigate]);

  const {
    showWarning: showInactivityWarning,
    secondsRemaining,
    dismissWarning
  } = useInactivityLogout(handleInactivityLogout, !!user);

  // Helper to open submenu (clears any pending close)
  const openSubMenu = (menuName) => {
    if (submenuCloseTimer.current) {
      clearTimeout(submenuCloseTimer.current);
      submenuCloseTimer.current = null;
    }
    setOpenSubDropdown(menuName);
  };

  // Helper to close submenu with delay
  const closeSubMenuWithDelay = () => {
    submenuCloseTimer.current = setTimeout(() => {
      setOpenSubDropdown(null);
    }, 150); // 150ms delay gives time to move to submenu
  };

  // Helper component for dropdown menu items with hover effect
  const DropdownItem = ({ icon, text, onClick, disabled = false }) => (
    <div
      style={{
        padding: '8px 16px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: disabled ? '#aaa' : '#333',
        transition: 'background 0.2s',
        opacity: disabled ? 0.6 : 1
      }}
      onMouseEnter={(e) => !disabled && (e.currentTarget.style.background = '#f0f0f0')}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      onClick={disabled ? null : onClick}
      title={disabled ? 'Coming soon' : ''}
    >
      {typeof icon === 'string' ? (
        <i className={icon} style={{ marginRight: '8px' }}></i>
      ) : (
        <span style={{ marginRight: '8px', display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
      )}
      {text}
      {disabled && <span style={{ fontSize: '0.7rem', marginLeft: '8px', color: '#999' }}>(Coming soon)</span>}
    </div>
  );

  // Helper component for section headers in dropdowns
  const SectionHeader = ({ text }) => (
    <div style={{
      padding: '6px 16px',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#666',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: '1px solid #eee',
      marginTop: '4px',
      backgroundColor: '#f8f9fa'
    }}>
      {text}
    </div>
  );

  // Check if user has any admin permissions for a technology
  const canAccessTechnology = (tech) => {
    return hasPermission('is_dev_admin') ||
           canManageConnections(tech) ||
           canViewComplianceReports(tech);
  };

  const TopNav = () => (
    <div>
      {/* Backdrop to close dropdown when clicking outside */}
      {openDropdown && (
        <div
          onClick={() => { setOpenDropdown(null); setOpenSubDropdown(null); }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
        />
      )}

      <nav style={{
        background: 'linear-gradient(90deg, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        padding: '10px 15px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        color: 'white',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setCurrentView('dashboard')}>
          {/* Database + Shield Logo */}
          <div style={{ position: 'relative', marginRight: '12px' }}>
            <i className="fas fa-shield-alt" style={{ color: '#4fd1c5', fontSize: '2.6rem' }}></i>
            <i className="fas fa-database" style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#2d3748',
              fontSize: '1.05rem'
            }}></i>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: '700', fontSize: '1.3rem', color: 'white', letterSpacing: '0.2em', lineHeight: '1' }}>D A M A</span>
            <span style={{ fontWeight: '300', fontSize: '0.85rem', color: '#a0aec0', marginTop: '0px' }}>Digital Access Management & Attestation</span>
          </div>
        </div>

        {/* Navigation Items */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <ul style={{ display: 'flex', listStyle: 'none', margin: 0, padding: 0, gap: '4px', alignItems: 'center' }}>
            {/* Dashboard */}
            {hasPermission('can_view_all_data') && (
              <li>
                <span
                  style={{
                    color: currentView === 'dashboard' ? '#4fd1c5' : 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    fontWeight: currentView === 'dashboard' ? 'bold' : 'normal'
                  }}
                  onClick={() => setCurrentView('dashboard')}
                >
                  <i className="fas fa-tachometer-alt" style={{ marginRight: '5px' }}></i>
                  Dashboard
                </span>
              </li>
            )}

            {/* Campaigns Dropdown */}
            {hasPermission('can_manage_campaigns') && (
              <li style={{ position: 'relative' }}>
                <span
                  style={{
                    color: 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    display: 'inline-block'
                  }}
                  onClick={() => setOpenDropdown(openDropdown === 'campaigns' ? null : 'campaigns')}
                >
                  <i className="fas fa-flag" style={{ marginRight: '5px' }}></i>
                  Campaigns
                  <i className="fas fa-chevron-down" style={{ marginLeft: '5px', fontSize: '0.8rem' }}></i>
                </span>
                {openDropdown === 'campaigns' && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    background: 'white',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                    minWidth: '220px',
                    zIndex: 1000,
                    marginTop: '5px'
                  }}>
                    <div style={{ padding: '8px 0' }}>
                      <DropdownItem icon="fas fa-plus-circle" text="Create New Campaign"
                        onClick={() => { setCurrentView('create-campaign'); setOpenDropdown(null); }} />
                      <DropdownItem icon="fas fa-list-ul" text="Select Existing Campaign"
                        onClick={() => { setCurrentView('select-campaign'); setOpenDropdown(null); }} />
                      <DropdownItem icon="fas fa-clipboard-check" text="Manage Attestations"
                        onClick={() => { setCurrentView('manage-attestations'); setOpenDropdown(null); }} />
                      <DropdownItem icon="fas fa-user-friends" text="Attester Replacement"
                        onClick={() => { setCurrentView('attester-replacement'); setOpenDropdown(null); }} />
                      <DropdownItem icon="fas fa-wrench" text="Attestation Actions"
                        onClick={() => { setCurrentView('attestation-actions'); setOpenDropdown(null); }} />
                    </div>
                  </div>
                )}
              </li>
            )}

            {/* My Attestations */}
            {hasPermission('can_perform_attestations') && (
              <li>
                <span
                  style={{
                    color: currentView === 'manager-attestations' ? '#4fd1c5' : 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    fontWeight: currentView === 'manager-attestations' ? 'bold' : 'normal'
                  }}
                  onClick={() => setCurrentView('manager-attestations')}
                >
                  <i className="fas fa-tasks" style={{ marginRight: '5px' }}></i>
                  My Attestations
                </span>
              </li>
            )}

            {/* Administration Dropdown - Technology Grouped */}
            {(hasPermission('is_dev_admin') || canManageAnyConnections()) && (
              <li style={{ position: 'relative' }}>
                <span
                  style={{
                    color: 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    display: 'inline-block'
                  }}
                  onClick={() => setOpenDropdown(openDropdown === 'admin' ? null : 'admin')}
                >
                  <i className="fas fa-cog" style={{ marginRight: '5px' }}></i>
                  Administration
                  <i className="fas fa-chevron-down" style={{ marginLeft: '5px', fontSize: '0.8rem' }}></i>
                </span>
                {openDropdown === 'admin' && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    background: 'white',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                    minWidth: '240px',
                    zIndex: 1000,
                    marginTop: '5px',
                    maxHeight: '70vh',
                    overflowY: 'auto'
                  }}>
                    <div style={{ padding: '4px 0' }}>
                      {/* Oracle Section */}
                      {canAccessTechnology('oracle') && (
                        <>
                          <SectionHeader text="Oracle" />
                          <DropdownItem icon="fas fa-plug" text="Database Connections"
                            onClick={() => { setCurrentView('oracle-connections'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Privilege Mappings"
                            onClick={() => { setCurrentView('oracle-privilege-mappings'); setOpenDropdown(null); }} />
                          {canManagePrivilegeMappings('oracle') && (
                            <DropdownItem icon="fas fa-shield-halved" text="Compliance Matrix"
                              onClick={() => { setCurrentView('oracle-compliance-matrix'); setOpenDropdown(null); }} />
                          )}
                        </>
                      )}

                      {/* PostgreSQL Section */}
                      {canAccessTechnology('postgresql') && (
                        <>
                          <SectionHeader text="PostgreSQL" />
                          <DropdownItem icon={<PostgresqlIcon size={16} />} text="Database Connections"
                            onClick={() => { setCurrentView('postgresql-connections'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Privilege Mappings"
                            onClick={() => { setCurrentView('postgresql-privilege-mappings'); setOpenDropdown(null); }} />
                          {canManagePrivilegeMappings('postgresql') && (
                            <DropdownItem icon="fas fa-shield-halved" text="Compliance Matrix"
                              onClick={() => { setCurrentView('postgresql-compliance-matrix'); setOpenDropdown(null); }} />
                          )}
                        </>
                      )}

                      {/* MSSQL Section */}
                      {canAccessTechnology('mssql') && (
                        <>
                          <SectionHeader text="SQL Server" />
                          <DropdownItem icon="fas fa-plug" text="Database Connections"
                            onClick={() => { setCurrentView('mssql-connections'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Privilege Mappings"
                            onClick={() => { setCurrentView('mssql-privilege-mappings'); setOpenDropdown(null); }} />
                          {canManagePrivilegeMappings('mssql') && (
                            <DropdownItem icon="fas fa-shield-halved" text="Compliance Matrix"
                              onClick={() => { setCurrentView('mssql-compliance-matrix'); setOpenDropdown(null); }} />
                          )}
                        </>
                      )}

                      {/* MySQL Section */}
                      {canAccessTechnology('mysql') && (
                        <>
                          <SectionHeader text="MySQL" />
                          <DropdownItem icon="fas fa-plug" text="Database Connections"
                            onClick={() => { setCurrentView('mysql-connections'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Privilege Mappings"
                            onClick={() => { setCurrentView('mysql-privilege-mappings'); setOpenDropdown(null); }} />
                          {canManagePrivilegeMappings('mysql') && (
                            <DropdownItem icon="fas fa-shield-halved" text="Compliance Matrix"
                              onClick={() => { setCurrentView('mysql-compliance-matrix'); setOpenDropdown(null); }} />
                          )}
                        </>
                      )}

                      {/* Cassandra Section */}
                      {canAccessTechnology('cassandra') && (
                        <>
                          <SectionHeader text="Cassandra" />
                          <DropdownItem icon="fas fa-plug" text="Database Connections"
                            onClick={() => { setCurrentView('cassandra-connections'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Privilege Mappings"
                            onClick={() => { setCurrentView('cassandra-privilege-mappings'); setOpenDropdown(null); }} />
                          {canManagePrivilegeMappings('cassandra') && (
                            <DropdownItem icon="fas fa-shield-halved" text="Compliance Matrix"
                              onClick={() => { setCurrentView('cassandra-compliance-matrix'); setOpenDropdown(null); }} />
                          )}
                        </>
                      )}

                      {/* Database Refresh — exposed to tech DBAs (ORACLE_DB_ADMIN,
                          MSSQL_DB_ADMIN, …) via their can_manage_<tech>_refresh_jobs
                          permission. Hidden when the full System section below
                          renders (dev admins) to avoid duplicating the menu item. */}
                      {!hasPermission('is_dev_admin') && getAccessibleTechnologies().some(t => canManageRefreshJobs(t)) && (
                        <>
                          <SectionHeader text="Operations" />
                          <DropdownItem icon="fas fa-sync-alt" text="Database Refresh"
                            onClick={() => { setCurrentView('database-refresh'); setOpenDropdown(null); }} />
                        </>
                      )}

                      {/* System Section - Only for dev admins */}
                      {hasPermission('is_dev_admin') && (
                        <>
                          <SectionHeader text="System" />
                          <DropdownItem icon="fas fa-sync-alt" text="Database Refresh"
                            onClick={() => { setCurrentView('database-refresh'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-users-cog" text="Enterprise Users — Sync"
                            onClick={() => { setCurrentView('refresh-enterprise-users'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-users-cog" text="Azure AD Groups"
                            onClick={() => { setCurrentView('azure-ad-groups'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-user-lock" text="User Management"
                            onClick={() => { setCurrentView('user-management'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-user-shield" text="Account Owners"
                            onClick={() => { setCurrentView('attester-override'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-users-cog" text="Database Owners"
                            onClick={() => { setCurrentView('database-owners'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-cube" text="Applications"
                            onClick={() => { setDeepLinkAppName(''); setCurrentView('applications'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-database" text="Database Mappings"
                            onClick={() => { setCurrentView('database-mappings'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-plug" text="All Connections"
                            onClick={() => { setCurrentView('database-connections'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-shield-alt" text="Compliance Frameworks"
                            onClick={() => { setCurrentView('compliance-frameworks'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-database" text="Database Types"
                            onClick={() => { setCurrentView('database-types'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-th" text="Privilege Matrix"
                            onClick={() => { setCurrentView('privilege-matrix'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-bell" text="Notification Manager"
                            onClick={() => { setCurrentView('notification-manager'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-archive" text="Evidence Retention"
                            onClick={() => { setCurrentView('evidence-retention'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Secret Settings"
                            onClick={() => { setCurrentView('secret-settings'); setOpenDropdown(null); }} />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            )}

            {/* Reports Dropdown - Technology Grouped */}
            {(hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns') ||
              canViewComplianceReports('oracle') || canViewComplianceReports('mssql') ||
              canViewComplianceReports('mysql') || canViewComplianceReports('postgresql') ||
              canViewComplianceReports('cassandra')) && (
              <li style={{ position: 'relative' }}>
                <span
                  style={{
                    color: 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    display: 'inline-block'
                  }}
                  onClick={() => setOpenDropdown(openDropdown === 'reports' ? null : 'reports')}
                >
                  <i className="fas fa-chart-bar" style={{ marginRight: '5px' }}></i>
                  Reports
                  <i className="fas fa-chevron-down" style={{ marginLeft: '5px', fontSize: '0.8rem' }}></i>
                </span>
                {openDropdown === 'reports' && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    background: 'white',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                    minWidth: '240px',
                    zIndex: 1000,
                    marginTop: '5px',
                    maxHeight: '70vh',
                    overflowY: 'auto'
                  }}>
                    <div style={{ padding: '4px 0' }}>
                      {/* Attestation Reports - for campaign managers */}
                      {(hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) && (
                        <>
                          <SectionHeader text="Attestation Reports" />
                          <DropdownItem icon="fas fa-file-alt" text="Pre-Attestation Report"
                            onClick={() => { setCurrentView('pre-attestation-baseline'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-file-check" text="Post-Attestation Report"
                            onClick={() => { setCurrentView('post-attestation-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-user-shield" text="Attester Audit Report"
                            onClick={() => { setCurrentView('attester-audit-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-camera-retro" text="Role & Privilege Baseline"
                            onClick={() => { setCurrentView('campaign-baseline'); setOpenDropdown(null); }} />
                        </>
                      )}

                      {/* Oracle Reports */}
                      {canViewComplianceReports('oracle') && (
                        <>
                          <SectionHeader text="Oracle" />
                          <DropdownItem icon="fas fa-user-lock" text="User Access Report"
                            onClick={() => { setCurrentView('oracle-access-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Password Security"
                            onClick={() => { setCurrentView('oracle-compliance'); setOpenDropdown(null); }} />
                        </>
                      )}

                      {/* PostgreSQL Reports */}
                      {canViewComplianceReports('postgresql') && (
                        <>
                          <SectionHeader text="PostgreSQL" />
                          <DropdownItem icon={<PostgresqlIcon size={16} />} text="User Access Report"
                            onClick={() => { setCurrentView('postgresql-access-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Password Security"
                            onClick={() => { setCurrentView('postgresql-compliance'); setOpenDropdown(null); }} />
                        </>
                      )}

                      {/* SQL Server Reports */}
                      {canViewComplianceReports('mssql') && (
                        <>
                          <SectionHeader text="SQL Server" />
                          <DropdownItem icon="fas fa-user-lock" text="User Access Report"
                            onClick={() => { setCurrentView('mssql-access-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-users" text="Group Access Report"
                            onClick={() => { setCurrentView('mssql-group-access-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Password Security"
                            onClick={() => { setCurrentView('mssql-compliance'); setOpenDropdown(null); }} />
                        </>
                      )}

                      {/* MySQL Reports */}
                      {canViewComplianceReports('mysql') && (
                        <>
                          <SectionHeader text="MySQL" />
                          <DropdownItem icon="fas fa-user-lock" text="User Access Report"
                            onClick={() => { setCurrentView('mysql-access-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Password Security"
                            onClick={() => { setCurrentView('mysql-compliance'); setOpenDropdown(null); }} />
                        </>
                      )}

                      {/* Cassandra Reports */}
                      {canViewComplianceReports('cassandra') && (
                        <>
                          <SectionHeader text="Cassandra" />
                          <DropdownItem icon="fas fa-user-lock" text="User Access Report"
                            onClick={() => { setCurrentView('cassandra-access-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-key" text="Password Security"
                            onClick={() => { setCurrentView('cassandra-compliance'); setOpenDropdown(null); }} />
                        </>
                      )}

                      {/* General Reports - for admins */}
                      {(hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) && (
                        <>
                          <SectionHeader text="General" />
                          {/* Dashboard Reports disabled: mix of mock + broken cards, superseded by
                              the individual report components below. Kept for future rewrite (deferred D4). */}
                          <DropdownItem icon="fas fa-chart-bar" text="Dashboard Reports (disabled)"
                            disabled
                            onClick={() => {}} />
                          <DropdownItem icon="fas fa-users" text="Enterprise Users — Report"
                            onClick={() => { setCurrentView('enterprise-users'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-exchange-alt" text="Mover-Leaver Report"
                            onClick={() => { setCurrentView('lateral-movement'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-check-double" text="C&A Report (Oracle)"
                            onClick={() => { setCurrentView('ca-report'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-check-double" text="C&A Report (SQL Server)"
                            onClick={() => { setCurrentView('ca-report-mssql'); setOpenDropdown(null); }} />
                          <DropdownItem icon="fas fa-robot" text="AI Query Assistant"
                            onClick={() => { setCurrentView('ai-query-assistant'); setOpenDropdown(null); }} />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            )}

            {/* Compliance dropdown removed - Password Security now under each technology in Reports */}

            {/* DAMA Pro */}
            {hasPermission('is_dev_admin') && (
              <li>
                <span
                  style={{
                    color: currentView === 'dama-pro' ? '#4fd1c5' : 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    fontWeight: currentView === 'dama-pro' ? 'bold' : 'normal'
                  }}
                  onClick={() => setCurrentView('dama-pro')}
                >
                  <i className="fas fa-gem" style={{ marginRight: '5px' }}></i>
                  DAMA Pro
                </span>
              </li>
            )}

            {/* DAMA Custom */}
            {hasPermission('is_dev_admin') && (
              <li>
                <span
                  style={{
                    color: currentView === 'custom-sql-executor' ? '#4fd1c5' : 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    fontWeight: currentView === 'custom-sql-executor' ? 'bold' : 'normal'
                  }}
                  onClick={() => setCurrentView('custom-sql-executor')}
                >
                  <i className="fas fa-terminal" style={{ marginRight: '5px' }}></i>
                  DAMA Custom
                </span>
              </li>
            )}

            {/* Enterprise IAG - Links to DAMA Enterprise */}
            {API_CONFIG.FEATURES.ENTERPRISE_IAG_ENABLED && hasPermission('is_dev_admin') && (
              <li>
                <a
                  href={API_CONFIG.FEATURES.ENTERPRISE_IAG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'white',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                  title="Open DAMA Enterprise - Identity & Access Governance Platform"
                >
                  <i className="fas fa-building" style={{ marginRight: '5px' }}></i>
                  Enterprise IAG
                  <i className="fas fa-external-link-alt" style={{ fontSize: '0.7rem', opacity: 0.7 }}></i>
                </a>
              </li>
            )}
          </ul>

          {/* Right side - User info and logout */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '15px' }}>
            {user && (
              <>
                <span style={{ color: 'white' }}>
                  <i className="fas fa-user-circle" style={{ marginRight: '8px', fontSize: '1.2rem' }}></i>
                  {user.full_name}
                </span>
                <button
                  onClick={() => logout(navigate)}
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-sign-out-alt" style={{ marginRight: '5px' }}></i>
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </nav>
    </div>
  );

  const navigateToCampaignWorkflow = (campaign) => {
    setSelectedCampaign(campaign);
    setCurrentView('campaign-workflow');
  };

  const navigateBackToCampaigns = () => {
    setSelectedCampaign(null);
    setCurrentView('select-campaign');
  };

  // Deep-link from the DB Compliance Matrix to the Applications registry, pre-filtered
  // to the clicked application. Only navigates when the user can view the Applications
  // screen (is_dev_admin); otherwise the link is rendered as plain text upstream.
  const navigateToApplication = (appName) => {
    if (!hasPermission('is_dev_admin')) return;
    setDeepLinkAppName(appName || '');
    setCurrentView('applications');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'applications':
        return hasPermission('is_dev_admin') ? <ApplicationDetails initialAppName={deepLinkAppName} /> : <Navigate to="/" />;
      case 'database-mappings':
        return hasPermission('is_dev_admin') ? <AppDatabaseMapping /> : <Navigate to="/" />;
      case 'create-campaign':
        return hasPermission('can_manage_campaigns') ? (
          <UARCampaigns
            key="campaign-create"
            onCampaignSelect={navigateToCampaignWorkflow}
            onNavigateToDashboard={() => setCurrentView('dashboard')}
            onGoBack={() => navigate(-1)}
            initialAction="create"
          />
        ) : <Navigate to="/" />;
      case 'campaign-workflow':
        return hasPermission('can_manage_campaigns') && selectedCampaign ? (
          <CampaignWorkflow campaign={selectedCampaign} onBack={navigateBackToCampaigns} />
        ) : (
          <Navigate to="/" />
        );
      case 'manage-attestations':
        return hasPermission('can_manage_campaigns') ? <ManageAttestations /> : <Navigate to="/" />;
      case 'manager-attestations':
        return hasPermission('can_perform_attestations') ? <ManagerAttestations /> : <Navigate to="/" />;
      case 'attester-replacement':
        return hasPermission('can_manage_campaigns') ? <AttesterReplacement /> : <Navigate to="/" />;
      case 'attestation-actions':
        return hasPermission('can_manage_campaigns') ? <AttestationActions /> : <Navigate to="/" />;
      case 'attester-override':
        return hasPermission('is_dev_admin') ? <AttesterOverride /> : <Navigate to="/" />;
      case 'database-owners':
        return hasPermission('is_dev_admin') ? <DatabaseOwners /> : <Navigate to="/" />;
      case 'compliance-frameworks':
        return hasPermission('is_dev_admin') ? <ComplianceFrameworks /> : <Navigate to="/" />;
      case 'database-types':
        return hasPermission('is_dev_admin') ? <DatabaseTypes /> : <Navigate to="/" />;
      case 'evidence-retention':
        return hasPermission('is_dev_admin') ? <RetentionSettings /> : <Navigate to="/" />;
      case 'secret-settings':
        return hasPermission('is_dev_admin') ? <SecretSettings /> : <Navigate to="/" />;
      // Per-technology connections pages are gated by that technology's
      // can_manage_<tech>_connections permission so DBAs (e.g. OraDBA) see their
      // own tech's connections without being is_dev_admin. is_dev_admin still
      // satisfies each check via canManageConnections' dev-admin passthrough.
      case 'database-connections':
        return (hasPermission('is_dev_admin') || canManageAnyConnections()) ? <DatabaseConnections onNavigate={setCurrentView} /> : <Navigate to="/" />;
      case 'oracle-connections':
        return canManageConnections('oracle') ? <OracleConnections /> : <Navigate to="/" />;
      case 'mssql-connections':
        return canManageConnections('mssql') ? <MssqlConnections /> : <Navigate to="/" />;
      case 'mysql-connections':
        return canManageConnections('mysql') ? <MySqlConnections /> : <Navigate to="/" />;
      case 'cassandra-connections':
        return canManageConnections('cassandra') ? <CassandraConnections /> : <Navigate to="/" />;
      case 'postgresql-connections':
        return canManageConnections('postgresql') ? <PostgresqlConnections /> : <Navigate to="/" />;
      // Database Refresh is accessible to any user who can manage refresh jobs for
      // at least one technology; the page itself filters jobs/executions to the
      // user's accessible technologies (see DatabaseRefresh.js).
      case 'database-refresh':
        return (hasPermission('is_dev_admin') || getAccessibleTechnologies().some(t => canManageRefreshJobs(t))) ? <DatabaseRefresh /> : <Navigate to="/" />;
      case 'refresh-enterprise-users':
        return hasPermission('is_dev_admin') ? <RefreshEnterpriseUsers /> : <Navigate to="/" />;
      case 'privilege-mappings':
        return hasPermission('is_dev_admin') ? <ManagePrivilegeMappings /> : <Navigate to="/" />;
      case 'privilege-matrix':
        return hasPermission('is_dev_admin') ? <PrivilegeMatrix /> : <Navigate to="/" />;
      case 'oracle-privilege-mappings':
        return canAccessTechnology('oracle') ? <ManagePrivilegeMappings key="oracle" technology="oracle" /> : <Navigate to="/" />;
      case 'postgresql-privilege-mappings':
        return canAccessTechnology('postgresql') ? <ManagePrivilegeMappings key="postgresql" technology="postgresql" /> : <Navigate to="/" />;
      case 'mssql-privilege-mappings':
        return canAccessTechnology('mssql') ? <ManagePrivilegeMappings key="mssql" technology="mssql" /> : <Navigate to="/" />;
      case 'mysql-privilege-mappings':
        return canAccessTechnology('mysql') ? <ManagePrivilegeMappings key="mysql" technology="mysql" /> : <Navigate to="/" />;
      case 'cassandra-privilege-mappings':
        return canAccessTechnology('cassandra') ? <ManagePrivilegeMappings key="cassandra" technology="cassandra" /> : <Navigate to="/" />;
      case 'oracle-compliance-matrix':
        return canManagePrivilegeMappings('oracle') ? <DBComplianceMatrix key="oracle" technology="oracle" onNavigateToApplication={hasPermission('is_dev_admin') ? navigateToApplication : undefined} /> : <Navigate to="/" />;
      case 'postgresql-compliance-matrix':
        return canManagePrivilegeMappings('postgresql') ? <DBComplianceMatrix key="postgresql" technology="postgresql" onNavigateToApplication={hasPermission('is_dev_admin') ? navigateToApplication : undefined} /> : <Navigate to="/" />;
      case 'mssql-compliance-matrix':
        return canManagePrivilegeMappings('mssql') ? <DBComplianceMatrix key="mssql" technology="mssql" onNavigateToApplication={hasPermission('is_dev_admin') ? navigateToApplication : undefined} /> : <Navigate to="/" />;
      case 'mysql-compliance-matrix':
        return canManagePrivilegeMappings('mysql') ? <DBComplianceMatrix key="mysql" technology="mysql" onNavigateToApplication={hasPermission('is_dev_admin') ? navigateToApplication : undefined} /> : <Navigate to="/" />;
      case 'cassandra-compliance-matrix':
        return canManagePrivilegeMappings('cassandra') ? <DBComplianceMatrix key="cassandra" technology="cassandra" onNavigateToApplication={hasPermission('is_dev_admin') ? navigateToApplication : undefined} /> : <Navigate to="/" />;
      case 'azure-ad-groups':
        return hasPermission('is_dev_admin') ? <AzureADGroups /> : <Navigate to="/" />;
      case 'user-management':
        return hasPermission('is_dev_admin') ? <UserManagement /> : <Navigate to="/" />;
      case 'select-campaign':
        return hasPermission('can_manage_campaigns') ? (
          <UARCampaigns
            key="campaign-select"
            onCampaignSelect={navigateToCampaignWorkflow}
            onNavigateToDashboard={() => setCurrentView('dashboard')}
            initialAction="select"
          />
        ) : <Navigate to="/" />;
      case 'reports':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <Reports /> : <Navigate to="/" />;
      case 'oracle-access-report':
        return canViewComplianceReports('oracle') ?
          <OracleUserAccessReport /> : <Navigate to="/" />;
      case 'mysql-access-report':
        return canViewComplianceReports('mysql') ?
          <MySQLUserAccessReport /> : <Navigate to="/" />;
      case 'cassandra-access-report':
        return canViewComplianceReports('cassandra') ?
          <CassandraUserAccessReport /> : <Navigate to="/" />;
      case 'mssql-access-report':
        return canViewComplianceReports('mssql') ?
          <MSSQLUserAccessReport /> : <Navigate to="/" />;
      case 'mssql-group-access-report':
        return canViewComplianceReports('mssql') ?
          <MSSQLGroupAccessReport /> : <Navigate to="/" />;
      case 'postgresql-access-report':
        return canViewComplianceReports('postgresql') ?
          <PostgresqlUserAccessReport /> : <Navigate to="/" />;
      case 'lateral-movement':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <LateralMovementReport /> : <Navigate to="/" />;
      case 'ai-query-assistant':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <AIQueryAssistant /> : <Navigate to="/" />;
      case 'enterprise-users':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <EnterpriseUsersReport /> : <Navigate to="/" />;
      case 'ca-report':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <CAReport /> : <Navigate to="/" />;
      case 'ca-report-mssql':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <CAReportMSSQL /> : <Navigate to="/" />;
      case 'pre-attestation-baseline':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <PreAttestationBaselineReport /> : <Navigate to="/" />;
      case 'campaign-baseline':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <CampaignBaselineReport /> : <Navigate to="/" />;
      case 'post-attestation-report':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <PostAttestationReport /> : <Navigate to="/" />;
      case 'attester-audit-report':
        return (hasPermission('can_view_all_data') || hasPermission('can_manage_campaigns')) ?
          <AttesterAuditReport /> : <Navigate to="/" />;
      case 'notification-manager':
        return hasPermission('is_dev_admin') ? <NotificationManager /> : <Navigate to="/" />;
      case 'custom-sql-executor':
        return hasPermission('is_dev_admin') ? <CustomSqlExecutor /> : <Navigate to="/" />;
      case 'dama-pro':
        return hasPermission('is_dev_admin') ? <DamaPro /> : <Navigate to="/" />;
      // Password Policy Compliance Views
      case 'oracle-compliance':
        return canViewComplianceReports('oracle') ? <OraclePasswordCompliance /> : <Navigate to="/" />;
      case 'mssql-compliance':
        return canViewComplianceReports('mssql') ? <MssqlPasswordCompliance /> : <Navigate to="/" />;
      case 'mysql-compliance':
        return canViewComplianceReports('mysql') ? <MysqlPasswordCompliance /> : <Navigate to="/" />;
      case 'postgresql-compliance':
        return canViewComplianceReports('postgresql') ? <PostgresqlPasswordCompliance /> : <Navigate to="/" />;
      case 'cassandra-compliance':
        return canViewComplianceReports('cassandra') ? <CassandraPasswordCompliance /> : <Navigate to="/" />;
      case 'dashboard':
      default:
        if (hasPermission('can_view_all_data')) {
          return <Dashboard />;
        } else if (hasPermission('can_perform_attestations')) {
          // Attesters should see their attestations by default
          setCurrentView('manager-attestations');
          return <ManagerAttestations />;
        }
        return <div className="p-5 text-center">Welcome to DAMA - Database Access Management & Attestation</div>;
    }
  };

  return (
    <div className="App">
      <TopNav />
      <div style={{ marginTop: '65px', height: 'calc(100vh - 65px)', overflow: 'auto' }}>
        {renderCurrentView()}
      </div>

      {/* Inactivity Warning Modal */}
      <InactivityWarningModal
        isOpen={showInactivityWarning}
        secondsRemaining={secondsRemaining}
        onStayLoggedIn={dismissWarning}
        onLogoutNow={handleInactivityLogout}
      />
    </div>
  );
};

/**
 * WHAT: Root component that wraps the entire app in Router, AuthProvider, and ErrorBoundary.
 * WHY:  Provides the foundational context (auth state, routing, error handling) that every
 *       downstream component depends on, ensuring consistent behavior across the application.
 */
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainApp />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
